import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { type INotification } from 'interfaces';

export enum DniCaptureMode {
  Native = 'native',
  Stream = 'stream',
  ImageCapture = 'image-capture',
}

export const isDniStreamCaptureMode = (mode: DniCaptureMode): boolean => {
  return mode !== DniCaptureMode.Native;
};

export const getDniCaptureModeLabel = (mode: DniCaptureMode): string => {
  return mode;
};

const isKnownDniCaptureMode = (value: unknown): value is DniCaptureMode => {
  return typeof value === 'string' && Object.values(DniCaptureMode).includes(value as DniCaptureMode);
};

export interface IUiState {
  isSidebarOpened: boolean;
  isFooterHidden: boolean;
  DNIimg: string | null;
  DNIImgBlob: Blob | null;
  ptp: boolean;
  isPtpRequired: boolean;
  shouldSkipLoanSlugFetch: boolean;
  loginLoading: boolean;
  hasCallIssuesAlert: boolean;
  dniCaptureMode: DniCaptureMode;
  hideLinksForTestUser: boolean;
  userNotification: INotification | null;
  autoApplyTimerFinished: boolean;
  documentVerification?: boolean;
  enableDniAsBlob: boolean;
  enableExplicitHeaderAndRetries: boolean;
  topLevelSelfieError: boolean;
  forceFBPopup: boolean;
  topLevelChangePinError: 'error' | 'success' | null;
  discountActivationError: boolean;
  documentError: boolean;
  extensionErrorMessage: string | null;
  bigDiscountNewClients: boolean;
  enableEWallets: boolean;
  isInitialOtpCooldown: boolean;
  showDetailedPaymentInfo: boolean;
}

const initialState: IUiState = {
  isSidebarOpened: false,
  isFooterHidden: false,
  DNIimg: null,
  DNIImgBlob: null,
  ptp: false,
  isPtpRequired: false,
  shouldSkipLoanSlugFetch: false,
  loginLoading: false,
  hasCallIssuesAlert: false,
  dniCaptureMode: DniCaptureMode.Stream,
  hideLinksForTestUser: true,
  userNotification: null,
  autoApplyTimerFinished: false,
  documentVerification: false,
  enableDniAsBlob: false,
  enableExplicitHeaderAndRetries: false,
  topLevelSelfieError: false,
  forceFBPopup: false,
  topLevelChangePinError: null,
  discountActivationError: false,
  documentError: false,
  extensionErrorMessage: null,
  bigDiscountNewClients: false,
  enableEWallets: false,
  isInitialOtpCooldown: false,
  showDetailedPaymentInfo: false,
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openSidebar: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpened = action.payload;
    },
    hideFooterUntilBESendsResponse: (state, action: PayloadAction<boolean>) => {
      state.isFooterHidden = action.payload;
    },
    setDNIimg: (state, action: PayloadAction<string | null>) => {
      state.DNIimg = action.payload;
    },
    setDNIImgBlob: (state, action: PayloadAction<Blob | null>) => {
      state.DNIImgBlob = action.payload;
    },
    setIsPtpRequired: (state, action: PayloadAction<boolean>) => {
      state.isPtpRequired = action.payload;
    },
    setPtp: (state, action: PayloadAction<boolean>) => {
      state.ptp = action.payload;
    },
    cleanupUi: () => {
      return initialState;
    },
    setSkipLoanSlugFetch: (state, action: PayloadAction<boolean>) => {
      state.shouldSkipLoanSlugFetch = action.payload;
    },
    setLoginLoader: (state, action: PayloadAction<boolean>) => {
      state.loginLoading = action.payload;
    },
    showHasCallIssuesAlert: (state, action: PayloadAction<boolean>) => {
      state.hasCallIssuesAlert = action.payload;
    },
    setUserNotification: (state, action: PayloadAction<INotification | null>) => {
      state.userNotification = action.payload;
    },
    setDniAsBlobEnabled: (state, action: PayloadAction<boolean>) => {
      state.enableDniAsBlob = action.payload;
    },

    setAllFeatureFlags: (state, action: PayloadAction<Record<string, any>>) => {
      const flags = action.payload;
      if (flags.dni_as_blob !== undefined) state.enableDniAsBlob = !!flags.dni_as_blob;
      if (isKnownDniCaptureMode(flags.dni_capture_mode)) {
        state.dniCaptureMode = flags.dni_capture_mode;
      }
      if (flags.hide_about_us_links !== undefined) state.hideLinksForTestUser = !!flags.hide_about_us_links;
      if (flags.enable_explicit_form_data_header_and_retry !== undefined)
        state.enableExplicitHeaderAndRetries = !!flags.enable_explicit_form_data_header_and_retry;
      if (flags.force_fb_popup !== undefined) state.forceFBPopup = !!flags.force_fb_popup;
      if (flags.big_discount_for_new_clients !== undefined)
        state.bigDiscountNewClients = !!flags.big_discount_for_new_clients;
      if (flags.enable_e_wallets !== undefined || flags.wallet_payout_allowed !== undefined) {
        state.enableEWallets = !!flags.enable_e_wallets && !!flags.wallet_payout_allowed;
      }
      if (flags.is_show_app_total_amount !== undefined)
        state.showDetailedPaymentInfo = !!flags.is_show_app_total_amount;
    },
    setAutoApplySend: (state, action: PayloadAction<boolean>) => {
      state.autoApplyTimerFinished = action.payload;
    },
    setDocumentVerification: (state, action: PayloadAction<boolean>) => {
      state.documentVerification = action.payload;
    },
    setTopLevelSelfieError: (state, action: PayloadAction<boolean>) => {
      state.topLevelSelfieError = action.payload;
    },
    setTopLevelChangePinError: (state, action: PayloadAction<'error' | 'success' | null>) => {
      state.topLevelChangePinError = action.payload;
    },
    setDiscountActivationError: (state, action: PayloadAction<boolean>) => {
      state.discountActivationError = action.payload;
    },
    setDocumentError: (state, action: PayloadAction<boolean>) => {
      state.documentError = action.payload;
    },
    setExtensionErrorMessage: (state, action: PayloadAction<string | null>) => {
      state.extensionErrorMessage = action.payload;
    },
    setInitialOtpCooldown: (state, action: PayloadAction<boolean>) => {
      state.isInitialOtpCooldown = action.payload;
    },
  },
});

export const {
  setPtp,
  setIsPtpRequired,
  cleanupUi,
  openSidebar,
  hideFooterUntilBESendsResponse,
  setDNIimg,
  setDNIImgBlob,
  setSkipLoanSlugFetch,
  setLoginLoader,
  showHasCallIssuesAlert,
  setUserNotification,
  setAllFeatureFlags,
  setAutoApplySend,
  setDocumentVerification,
  setDniAsBlobEnabled,
  setTopLevelSelfieError,
  setTopLevelChangePinError,
  setDiscountActivationError,
  setDocumentError,
  setExtensionErrorMessage,
  setInitialOtpCooldown,
} = uiSlice.actions;

export const selectUi = (state: RootState) => state.ui;

export default uiSlice.reducer;
